import { m } from 'framer-motion';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import CustomPopover, { usePopover } from '../../../components/custom-popover';
import { varHover } from '../../../components/layout-components/animate';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { logout } from '../../../modules/auth/authenticationSlice';
import ByzMuiButton from '../../../components/core-components/mui/buttons/ByzMuiButton';
import ByzMuiTypography from '../../../components/core-components/mui/inputs/ByzMuiTypography';
import { useTranslate } from 'src/locales';
import Iconify from '../../../components/iconify';

import AccountAboutDialog from './about-dialog';
import AccountLogoutDialog from './logout-dialog';
import { Button } from '@mui/material';

// ----------------------------------------------------------------------

export default function AccountPopover() {
    const { t } = useTranslate();
    const dispatch = useAppDispatch();
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [aboutDialogOpen, setAboutDialogOpen] = useState(false);

    const authState = useAppSelector(state => state.auth);
    const { user } = {
        user: {
            displayName: authState.user?.name + " " + authState.user?.surname,
            email: authState.user?.email,
            photoURL: ""
        }
    };
    const popover = usePopover();

    const handleLogoutClick = () => {
        setLogoutDialogOpen(true);
        popover.onClose();
    };

    const handleLogoutConfirm = async () => {
        try {
            dispatch(logout());
            setLogoutDialogOpen(false);
            window.location.href = '/';
        } catch (error) {
            console.error('❌ Logout hatası:', error);
        }
    };

    const handleLogoutCancel = () => {
        setLogoutDialogOpen(false);
    };

    const handleAboutClick = () => {
        setAboutDialogOpen(true);
        popover.onClose();
    };

    const handleAboutClose = () => {
        setAboutDialogOpen(false);
    };

    return (
        <>
            <Button
                onClick={(e: any) => popover.onOpen(e)}
                variant="outlined"
                sx={{
                    height: 40,
                    px: 1.5,
                    borderRadius: 1.25,
                    bgcolor: '#fff',
                    borderColor: '#F7F7F7',
                    fontWeight: '400',
                    '&:hover': {
                        bgcolor: 'rgba(99, 180, 255, 0.1)',
                    },
                }}
                endIcon={
                    <Iconify
                        icon={popover.open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                        width={18}
                    />
                }
            >
                {user?.displayName}
            </Button>

            <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 220, p: 0 }}>
                <Box sx={{ p: 2, pb: 1.5 }}>
                    <ByzMuiTypography variant="subtitle2" noWrap>
                        {user?.displayName}
                    </ByzMuiTypography>

                    <ByzMuiTypography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                        {user?.email}
                    </ByzMuiTypography>
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Stack sx={{ p: 1 }}>
                    <MenuItem onClick={handleAboutClick}>
                        <ListItemIcon>
                            <img src="/assets/icons/header/about.svg" alt="" />
                        </ListItemIcon>
                        <ListItemText primary={t('profile-menu-items.about')} />
                        <Iconify icon="eva:chevron-right-fill" width={16} />
                    </MenuItem>
                </Stack>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <MenuItem
                    onClick={handleLogoutClick}
                    sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'red' }}
                >
                    <ListItemIcon>
                        <img src="/assets/icons/header/logout.svg" alt="" />
                    </ListItemIcon>
                    <ListItemText primary={t('profile-menu-items.logout')} />
                </MenuItem>
            </CustomPopover>

            <AccountAboutDialog
                open={aboutDialogOpen}
                onClose={handleAboutClose}
            />

            <AccountLogoutDialog
                open={logoutDialogOpen}
                onClose={handleLogoutCancel}
                onConfirm={handleLogoutConfirm}
            />
        </>
    );
}
